import { rufe } from './client';

/** BUC-131: die Stände eines Absagefalls. */
export type Fallstand =
  | 'offen'
  | 'informiert'
  | 'kontaktiert'
  | 'umgebucht'
  /* RNG-37, BUC-44 (AP-FA-05): storniert, eine Rückzahlung ist noch nicht ausgeführt. */
  | 'erstattung_offen'
  | 'erstattet'
  | 'storniert';

export const FALLSTANDNAME: Readonly<Record<Fallstand, string>> = {
  offen: 'offen',
  informiert: 'informiert',
  kontaktiert: 'Kontakt aufgenommen',
  umgebucht: 'umgebucht',
  erstattung_offen: 'Rückzahlung offen',
  erstattet: 'erstattet',
  storniert: 'storniert',
};

/** Abschließend behandelt (BUC-132). `erstattung_offen` fehlt mit Absicht (RNG-37). */
export const ABSCHLIESSEND: readonly Fallstand[] = ['umgebucht', 'erstattet', 'storniert'];

export type Absagefall = {
  readonly id: string;
  readonly termin: string;
  readonly buchung: string;
  readonly buchungsnummer: string;
  readonly plaetze: number;
  readonly kunde: string;
  readonly kundenname: string;
  readonly kundennummer: string;
  readonly email: string | null;
  readonly telefon: string | null;
  readonly stand: Fallstand;
  readonly informiertAm: string | null;
  readonly kontaktAm: string | null;
  readonly abgeschlossenAm: string | null;
  readonly eskaliertAm: string | null;
  readonly zielTerminBeginn: string | null;
  readonly belegnummer: string | null;
  readonly notiz: string | null;
  readonly gezahlt: string;
  readonly rechnungen: readonly string[];
};

/** RES-88: ein bei der Absage stornierter Einsatz und was er kostet. */
export type StornierterEinsatz = {
  readonly belegung: string;
  readonly ressource: string;
  readonly art: string;
  readonly faellig: boolean;
  readonly betrag: string;
};

export type Absage = {
  readonly termin: string;
  readonly produkt: string;
  readonly beginn: string;
  readonly ort: string | null;
  readonly grund: string | null;
  readonly abgesagtAm: string | null;
  readonly offen: number;
  readonly abgeschlossen: boolean;
  readonly faelle: readonly Absagefall[];
  /** RES-88: die stornierten Einsätze mit ihrer Ausfallvergütung. */
  readonly einsaetze: readonly StornierterEinsatz[];
  readonly ausfallSumme: string;
};

export type Alternative = {
  readonly id: string;
  readonly beginn: string;
  readonly ende: string;
  readonly frei: number;
  readonly ort: { readonly kennung: string; readonly bezeichnung: string } | null;
};

export type Erstattungsergebnis = {
  readonly stand: 'erstattet' | 'erstattung_offen' | 'storniert';
  readonly beleg: string | null;
  readonly belegnummer: string | null;
  /** RNG-37: `angelegt` heißt, die Rückzahlung muss noch jemand ausführen. */
  readonly rueckzahlungen: readonly {
    readonly betrag: string;
    readonly art: string;
    readonly status: 'angelegt' | 'bestaetigt' | 'fehlgeschlagen' | 'abgebrochen';
    readonly vorgang: string;
  }[];
  readonly hinweise: readonly string[];
};

export async function absagen(alle: boolean): Promise<readonly Absage[]> {
  const antwort = await rufe<{ readonly absagen: readonly Absage[] }>(
    `/absagen${alle ? '?alle=true' : ''}`,
  );
  return antwort.absagen;
}

export async function alternativen(fall: string): Promise<readonly Alternative[]> {
  const antwort = await rufe<{ readonly termine: readonly Alternative[] }>(
    `/absagen/faelle/${fall}/alternativen`,
  );
  return antwort.termine;
}

export async function kontaktAufgenommen(fall: string, notiz: string | null): Promise<void> {
  await rufe(`/absagen/faelle/${fall}/kontakt`, { verfahren: 'POST', rumpf: { notiz } });
}

export async function umbuchen(fall: string, zielTermin: string, notiz: string | null): Promise<void> {
  await rufe(`/absagen/faelle/${fall}/umbuchen`, {
    verfahren: 'POST',
    rumpf: { zielTermin, notiz },
  });
}

export async function stornierenUndErstatten(
  fall: string,
  notiz: string | null,
): Promise<Erstattungsergebnis> {
  return rufe<Erstattungsergebnis>(`/absagen/faelle/${fall}/stornieren`, {
    verfahren: 'POST',
    rumpf: { notiz },
  });
}
